import { useState } from "react";
import { Copy, Mail, MessageCircle, RefreshCw, ShieldOff, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DviAPI, extractError } from "@/lib/api";

function buildPublicUrl(token, fallback) {
    if (!token) return "";
    if (fallback && fallback.startsWith("http")) return fallback;
    if (typeof window !== "undefined") {
        return `${window.location.origin}/inspection/${token}`;
    }
    return fallback || "";
}

export function ShareLinkModal({ open, onOpenChange, inspection, jobLabel, onShareUpdated }) {
    const [busy, setBusy] = useState(false);
    const token = inspection?.customer_share_token;
    const url = buildPublicUrl(token, inspection?.share_url);

    async function ensureToken() {
        setBusy(true);
        try {
            const r = await DviAPI.issueShare(inspection.id);
            onShareUpdated?.({ token: r.token, share_url: r.share_url, expires_at: r.expires_at });
            return r;
        } catch (err) {
            toast.error(extractError(err));
            throw err;
        } finally {
            setBusy(false);
        }
    }

    async function handleRevoke() {
        setBusy(true);
        try {
            await DviAPI.revokeShare(inspection.id);
            onShareUpdated?.({ token: null, share_url: null, expires_at: null });
            toast.success("Share link revoked");
            onOpenChange(false);
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setBusy(false);
        }
    }

    async function copyUrl() {
        let u = url;
        if (!u) {
            const r = await ensureToken();
            u = buildPublicUrl(r.token, r.share_url);
        }
        try {
            await navigator.clipboard.writeText(u);
            toast.success("Link copied");
        } catch {
            toast.success(u);
        }
    }

    const message = `Inspection report for ${jobLabel || "your vehicle"}: ${url}`;
    const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;
    const mailHref = `mailto:?subject=${encodeURIComponent("Vehicle inspection report")}&body=${encodeURIComponent(message)}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="dvi-share-modal">
                <DialogHeader>
                    <DialogTitle>Share inspection with customer</DialogTitle>
                    <DialogDescription>
                        A read-only link. The customer can review findings, approve or reject each item,
                        and download a PDF copy of the report.
                    </DialogDescription>
                </DialogHeader>

                {url ? (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <Input
                                readOnly
                                value={url}
                                data-testid="dvi-share-url"
                                onFocus={(e) => e.target.select()}
                                className="font-mono text-xs"
                            />
                            <Button size="sm" variant="outline" onClick={copyUrl} data-testid="dvi-share-copy">
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <Button asChild variant="outline" size="sm">
                                <a
                                    href={waHref} target="_blank" rel="noreferrer"
                                    data-testid="dvi-share-whatsapp"
                                >
                                    <MessageCircle className="mr-1.5 h-4 w-4" />
                                    WhatsApp
                                </a>
                            </Button>
                            <Button asChild variant="outline" size="sm">
                                <a href={mailHref} data-testid="dvi-share-email">
                                    <Mail className="mr-1.5 h-4 w-4" />
                                    Email
                                </a>
                            </Button>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <Button
                                variant="ghost" size="sm" onClick={ensureToken} disabled={busy}
                                data-testid="dvi-share-reissue"
                            >
                                <RefreshCw className="mr-1.5 h-3 w-3" />
                                Reissue link
                            </Button>
                            <Button
                                variant="ghost" size="sm" onClick={handleRevoke} disabled={busy}
                                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                                data-testid="dvi-share-revoke"
                            >
                                <ShieldOff className="mr-1.5 h-3 w-3" />
                                Revoke
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 pt-2">
                        <p className="text-sm text-zinc-600 dark:text-zinc-300">
                            No active share link yet. Generate one to send the customer.
                        </p>
                        <Button onClick={ensureToken} disabled={busy} data-testid="dvi-share-generate">
                            {busy ? "Generating…" : "Generate Share Link"}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
